import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import AudioPreview from 'components/UI/AudioPreview';
import TextToggle from 'components/UI/TextToggle';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useGetAllAvailableDeviceTypesQuery } from 'services/devices/devices.api';
import { displayStatusOptions } from 'utils/devices/icon.utils';
import { buildDeviceSelectOptionsForDeviceType } from '../../../conditions/DeviceCondition/utils/deviceCondition.utils';

export function VerifyToggleFlashingLights(dataObj) {
  if (!dataObj) return false;
  return !(dataObj?.status === '' || dataObj?.comment === '');
}

export default function ToggleFlashingLights({
  onUpdate,
  deviceList,
  dataObj,
  group,
  readOnly = false,
}) {
  const isOn = ![0, 7, 8].includes(dataObj?.status);
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const [intervalEnabled, setIntervalEnabled] = useState(!!dataObj?.interval);
  const [selectedToneRelayId, setSelectedToneRelayId] = useState(isOn ? dataObj?.status : null);
  const deviceOptions = buildDeviceSelectOptionsForDeviceType(deviceList, dataObj.deviceType);

  useEffect(() => {
    if (dataObj?.status === undefined || dataObj?.status === null) {
      onUpdate({ status: 0 });
    }

    if (group) {
      const firstDeviceOfGroupType = deviceList.find((device) => device.device_type === group.type);
      if (firstDeviceOfGroupType) {
        onUpdate({
          selectedDevice: {
            ...firstDeviceOfGroupType,
            data: [firstDeviceOfGroupType.data[0]],
          },
        });
      }
    }
  }, [group]);

  const resolvedType =
    dataObj?.selectedDevice?.device_type || dataObj?.deviceType || group?.type || '';

  const normalizedType = String(resolvedType).toLowerCase();

  const isSirenType =
    normalizedType.includes('siren/giant voice') ||
    normalizedType.includes('siren') ||
    normalizedType.includes('giant voice');

  const isManualRoadClosure = normalizedType.includes('manual road closure');
  const isFlashing =
    normalizedType.includes('flashing lights') || normalizedType.includes('flashing_light');

  const getStatusOptions = () => {
    if (isManualRoadClosure) return ['Open', 'Closed'];
    if (isFlashing) return ['Idle', 'Alerting'];
    if (isSirenType) return ['Idle', 'Alerting'];

    const shared = displayStatusOptions({
      ...dataObj,
      deviceType: resolvedType,
      selectedDevice:
        dataObj?.selectedDevice || (resolvedType ? { device_type: resolvedType } : undefined),
    });

    return shared && shared.length ? shared : ['Off', 'On'];
  };

  const statusOptions = getStatusOptions();

  // Resolve device type info from selectedDevice, falling back to group.type for group actions.
  // dataObj.deviceType is stored as "SystemType DeviceType" (e.g. "RescAlert Devices Technology Bridge_Siren/Giant Voice")
  const sirenDeviceType =
    dataObj?.selectedDevice?.device_type ||
    group?.type ||
    (dataObj?.deviceType ? dataObj.deviceType.replace(/^RescAlert Devices\s+/i, '') : '');
  const sirenSystemType =
    dataObj?.selectedDevice?.system_type || (isSirenType ? 'RescAlert Devices' : '');

  const sirenTypes =
    sirenDeviceType && sirenSystemType
      ? deviceTypes?.[sirenSystemType]?.[sirenDeviceType]?.sirenTypes || []
      : [];

  useEffect(() => {
    if (sirenTypes.length > 0 && selectedToneRelayId == null) {
      setSelectedToneRelayId(sirenTypes[0].relay_id);
    }
  }, [sirenTypes.length]);

  const selectedTone = sirenTypes.find((t) => t.relay_id == selectedToneRelayId) || sirenTypes[0];
  const sirenToneOptions = sirenTypes.map((tone) => ({
    value: tone.relay_id,
    label: tone.displayName,
    tone,
  }));

  return (
    <Card className="global-action-builder-row rounded-2">
      <Card.Body className="d-flex flex-column gap-3">
        {/* Device Alias (hidden for groups) */}
        {!group && (
          <Row className="align-items-center">
            <Col xs={12} md={4} className="fw-semibold">
              Device Alias
            </Col>
            <Col xs={12} md={8}>
              <BaseSelect
                wrapperClassName="flex-fill"
                isSearchable
                isClearable
                classNames={{
                  control: () => 'global-action-builder-field',
                }}
                selectProps={{
                  options: deviceOptions,
                  value: findSelectOption(deviceOptions, dataObj.node_id),
                  placeholder: 'Select',
                  isDisabled: readOnly,
                  onChange: (option) => {
                    const device = option?.device;
                    onUpdate({
                      node_id: option?.value ?? '',
                      selectedDevice: device ? { ...device, data: [device.data[0]] } : null,
                      coordinates: [device?.coordinates?.lat, device?.coordinates?.lng],
                    });
                  },
                }}
              />
            </Col>
          </Row>
        )}

        {/* Siren Audio Row — tone dropdown + actions, shown above status for siren types */}
        {isSirenType && sirenTypes.length > 0 && (
          <Card className="global-action-builder-editor">
            <Card.Body className="p-3">
              <Row className="g-3 align-items-center">
                <Col xs={12} md={4}>
                  <Form.Label className="m-0 fw-semibold">Siren Tone</Form.Label>
                </Col>
                <Col xs={12} md={8} className="d-flex flex-wrap align-items-center gap-2">
                  <BaseSelect
                    wrapperClassName="flex-fill"
                    isSearchable
                    classNames={{ control: () => 'global-action-builder-field' }}
                    selectProps={{
                      'aria-label': 'Siren Tone',
                      options: sirenToneOptions,
                      value: findSelectOption(sirenToneOptions, selectedTone?.relay_id),
                      isDisabled: readOnly,
                      onChange: (option) => {
                        const tone = option?.tone ?? null;
                        setSelectedToneRelayId(tone?.relay_id ?? null);
                        if (isOn) onUpdate({ status: tone?.relay_id });
                      },
                    }}
                  />

                  <AudioPreview tone={selectedTone} />

                  <Button
                    size="sm"
                    variant={dataObj?.status === 7 ? 'warning' : 'outline-secondary'}
                    className="fw-semibold"
                    onClick={() => onUpdate({ status: dataObj?.status === 7 ? 0 : 7 })}
                  >
                    Cancel Audio
                  </Button>

                  <Button
                    size="sm"
                    variant={dataObj?.status === 8 ? 'danger' : 'outline-secondary'}
                    className="fw-semibold"
                    onClick={() => onUpdate({ status: dataObj?.status === 8 ? 0 : 8 })}
                  >
                    Power Cycle Device
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Status + Actions */}
        <Row className="align-items-center">
          <Col xs={12} md={4} className="fw-semibold">
            Status
          </Col>
          <Col xs={12} md={8} className="d-flex flex-wrap gap-2">
            <TextToggle
              options={statusOptions}
              value={isOn}
              setValue={(value) => {
                if (isSirenType) {
                  onUpdate({ status: value ? (selectedTone?.relay_id ?? 2) : 0 });
                } else {
                  onUpdate({ status: value ? 1 : 0 });
                }
              }}
              pill
              groupStyle={{
                border: `2px solid var(--color-status-${isOn ? 'danger' : 'success'})`,
                borderRadius: '999px',
                background: 'transparent',
              }}
              activeVariant={isOn ? 'danger' : 'success'}
              inactiveVariant="outline-secondary"
              buttonClassName="fw-semibold"
            />

            {isSirenType && !sirenTypes.length && (
              <>
                <Button
                  size="sm"
                  variant={dataObj?.status === 7 ? 'warning' : 'outline-secondary'}
                  className="fw-semibold"
                  onClick={() => onUpdate({ status: dataObj?.status === 7 ? 0 : 7 })}
                >
                  Cancel Audio
                </Button>

                <Button
                  size="sm"
                  variant={dataObj?.status === 8 ? 'danger' : 'outline-secondary'}
                  className="fw-semibold"
                  onClick={() => onUpdate({ status: dataObj?.status === 8 ? 0 : 8 })}
                >
                  Power Cycle Device
                </Button>
              </>
            )}
          </Col>
        </Row>

        {/* Repeat controls */}
        {isSirenType && isOn && (
          <Row className="g-3 align-items-center">
            <Col xs={12} md={4} className="fw-semibold">
              Repeat
            </Col>

            <Col xs={12} md={8} className="d-flex flex-wrap align-items-center gap-3">
              <TextToggle
                options={['Off', 'On']}
                value={intervalEnabled}
                setValue={(enabled) => {
                  setIntervalEnabled(enabled);

                  if (enabled) {
                    const safeInterval = Math.min(
                      7200,
                      Math.max(30, Number(dataObj?.interval || 30)),
                    );
                    onUpdate({ interval: safeInterval });
                  } else {
                    onUpdate({ interval: null });
                  }
                }}
                pill
                groupStyle={{
                  borderRadius: '999px',
                  background: 'var(--color-base)',
                  overflow: 'hidden',
                }}
                activeVariant="primary"
                inactiveVariant="outline-light"
                buttonClassName="fw-semibold"
              />

              <div className="d-flex align-items-baseline gap-2">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip className="global-action-builder-tooltip">
                      This controls how often the event should repeat, in seconds. Valid range:
                      30–7200.
                    </Tooltip>
                  }
                >
                  <div className="d-flex align-items-center">
                    <IoInformationCircleOutline size={22} />
                  </div>
                </OverlayTrigger>

                <Form.Control
                  className="global-action-builder-field"
                  type="number"
                  min={30}
                  max={7200}
                  disabled={!intervalEnabled}
                  value={dataObj?.interval ?? 30}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const num = Number(raw);
                    if (raw === '') return onUpdate({ interval: '' });
                    if (!Number.isNaN(num)) onUpdate({ interval: num });
                  }}
                  onBlur={() => {
                    if (!intervalEnabled) return;
                    const clamped = Math.min(7200, Math.max(30, Number(dataObj?.interval || 30)));
                    onUpdate({ interval: clamped });
                  }}
                />
              </div>
            </Col>
          </Row>
        )}

        {/* Comments */}
        <Row className="g-3">
          <Col xs={12} md={4} className="fw-semibold">
            Comments
          </Col>
          <Col xs={12} md={8}>
            <Form.Control
              as="textarea"
              rows={4}
              value={dataObj.comment || ''}
              onChange={(e) => onUpdate({ comment: e.target.value })}
              placeholder="(Optional)"
              className="global-action-builder-field"
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
